/**
 * Pure helpers for the "Report a bug" dialog (2026-09-30): field bounds,
 * validation, dirty check, and request-body building. No React, no I/O, so the
 * bounds can be unit-tested against the Backend contract (POST /bug-reports).
 */

export const BUG_REPORT_LIMITS = Object.freeze({
  titleMin: 5,
  titleMax: 200,
  descriptionMin: 10,
  descriptionMax: 2000,
  stepsMax: 2000,
  emailMax: 254,
  pageUrlMax: 2048,
  userAgentMax: 512,
});

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** @returns {{title: string, description: string, steps: string, email: string}} */
export function emptyBugReportFields(email = '') {
  return { title: '', description: '', steps: '', email: email || '' };
}

/**
 * Length in Unicode code points of the trimmed value (the server counts code
 * points, so an emoji is 1, not 2 UTF-16 units).
 * @param {string} value
 */
export function countChars(value) {
  return [...value.trim()].length;
}

function checkRequired(label, value, min, max) {
  const len = countChars(value);
  if (len === 0) return `${label} is required`;
  if (len < min) return `${label} must be at least ${min} characters`;
  if (len > max) return `${label} must be ${max} characters or fewer`;
  return '';
}

/**
 * Validates the form fields against the Backend bounds (lengths are measured on
 * the trimmed value, which is also what gets sent).
 * @param {{title: string, description: string, steps: string, email: string}} fields
 * @returns {{title?: string, description?: string, steps?: string, email?: string}} empty when valid
 */
export function validateBugReport(fields) {
  const L = BUG_REPORT_LIMITS;
  const errors = {};
  const title = checkRequired('Title', fields.title, L.titleMin, L.titleMax);
  if (title) errors.title = title;
  const description = checkRequired(
    'What happened',
    fields.description,
    L.descriptionMin,
    L.descriptionMax
  );
  if (description) errors.description = description;
  if (countChars(fields.steps) > L.stepsMax) {
    errors.steps = `Steps must be ${L.stepsMax} characters or fewer`;
  }
  const email = fields.email.trim();
  if (email && (countChars(email) > L.emailMax || !EMAIL_RE.test(email))) {
    errors.email = 'Enter a valid email address or leave it blank';
  }
  return errors;
}

/**
 * True when the user has typed anything (or changed the prefilled e-mail).
 * @param {{title: string, description: string, steps: string, email: string}} fields
 * @param {string} [prefillEmail]
 */
export function isBugReportDirty(fields, prefillEmail = '') {
  return Boolean(
    fields.title.trim()
    || fields.description.trim()
    || fields.steps.trim()
    || fields.email.trim() !== (prefillEmail || '').trim()
  );
}

/**
 * Page URL and browser string attached to every report, clamped to the
 * Backend bounds. The URL is origin + pathname only: the query string and hash
 * are dropped because an OAuth `code`/`state` can linger there (2026-09-30).
 * Returns '' for values the environment does not provide.
 * @returns {{pageUrl: string, userAgent: string}}
 */
export function captureBugReportContext() {
  const L = BUG_REPORT_LIMITS;
  const loc = typeof window !== 'undefined' ? window.location : null;
  const origin = loc?.origin && loc.origin !== 'null' ? loc.origin : '';
  const href = loc ? `${origin}${loc.pathname || ''}` : '';
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
  return { pageUrl: href.slice(0, L.pageUrlMax), userAgent: ua.slice(0, L.userAgentMax) };
}

/**
 * Builds the POST /bug-reports body. Optional fields are omitted when empty.
 * @param {{title: string, description: string, steps: string, email: string}} fields
 * @param {{recaptchaToken: string, pageUrl?: string, userAgent?: string}} extra
 */
export function buildBugReportBody(fields, { recaptchaToken, pageUrl, userAgent }) {
  const body = {
    title: fields.title.trim(),
    description: fields.description.trim(),
    recaptcha_token: recaptchaToken,
    app: 'guardian',
  };
  const steps = fields.steps.trim();
  if (steps) body.steps = steps;
  const email = fields.email.trim();
  if (email) body.email = email;
  if (pageUrl) body.page_url = pageUrl;
  if (userAgent) body.user_agent = userAgent;
  return body;
}

const MAX_VALIDATION_MESSAGE = 300;

/**
 * Builds the validation copy from a FastAPI-style `detail` array
 * ([{loc, msg}]). Prefers the field names (last `loc` segment); falls back to
 * the joined messages, capped.
 * @param {unknown} detail
 */
function validationMessage(detail) {
  const items = Array.isArray(detail) ? detail : [];
  const fields = [];
  for (const item of items) {
    const loc = Array.isArray(item?.loc) ? item.loc : [];
    const name = loc[loc.length - 1];
    if (typeof name === 'string' && name && !fields.includes(name)) fields.push(name);
  }
  if (fields.length > 0) {
    return `Check these fields: ${fields.join(', ')}`.slice(0, MAX_VALIDATION_MESSAGE);
  }
  const joined = items
    .map((item) => (typeof item?.msg === 'string' ? item.msg : ''))
    .filter(Boolean)
    .join('; ');
  return (joined || 'Check the fields and try again').slice(0, MAX_VALIDATION_MESSAGE);
}

/**
 * Maps a submitBugReport result to UI copy. 429 is never retried by the client.
 * The Backend reports request-validation failures as 400 with a `detail` array
 * (Interlinked-Backend main.py); 422 is also treated as validation for safety.
 * Status 0 (timeout / network) may still have reached the server, so the copy
 * tells the user to wait rather than resend, which would burn the 5/hour budget.
 * @param {{success?: boolean, status?: number, message?: string, detail?: unknown}} res
 * @returns {{kind: 'sent'|'rate_limited'|'validation'|'session'|'unconfirmed'|'error', message: string}}
 */
export function classifyBugReportResult(res) {
  const status = res?.status || 0;
  if (res?.success && status >= 200 && status < 300) return { kind: 'sent', message: '' };
  if (status === 429) {
    return {
      kind: 'rate_limited',
      message: 'Too many reports from this account — try again in an hour.',
    };
  }
  if (status === 422 || (status === 400 && Array.isArray(res?.detail))) {
    return { kind: 'validation', message: validationMessage(res?.detail) };
  }
  if (status === 401) {
    return {
      kind: 'session',
      message: 'Your session has expired. Sign in again to send this report.',
    };
  }
  if (status === 0) {
    return {
      kind: 'unconfirmed',
      message: "We couldn't confirm the report was sent. Wait a minute before resending.",
    };
  }
  return {
    kind: 'error',
    message: res?.message || 'Could not send the report. Please try again.',
  };
}
